import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Plus,
  Upload,
  Download,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
  X,
  CheckSquare,
  Square,
  Settings,
  Phone,
  Mail,
  Link as LinkIcon,
  Hash,
  Calendar,
  Type,
  Sparkles
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { exportsService } from '@/services/exports'
import InlineEditCell from '@/components/InlineEditCell'
import BulkOperationsModal from '@/components/BulkOperationsModal'
import ColumnFilter from '@/components/ColumnFilter'
import ColumnSort from '@/components/ColumnSort'
import ExportModal from '@/components/ExportModal'
import { useAuth } from '@/context/AuthContext'
import { USER_ROLES } from '@/utils/constants'
import toast from 'react-hot-toast'

const RecordsTab = ({ dataInput }) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  
  // State management
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterColumn, setFilterColumn] = useState('')
  const [filterValue, setFilterValue] = useState('')
  const [filterOperator, setFilterOperator] = useState('eq')
  const [editingCell, setEditingCell] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)

  // Bulk operations state
  const [selectedRecords, setSelectedRecords] = useState([])
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false)

  // Column-based filtering state
  const [columnFilters, setColumnFilters] = useState([])


  const [showColumnFilters, setShowColumnFilters] = useState(false)

  // Column sorting state
  const [currentSort, setCurrentSort] = useState(null)

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false)

  // Refs for sticky header implementation
  const tableRef = useRef(null)
  const headerRef = useRef(null)
  const scrollContainerRef = useRef(null)

  // Mobile-optimized sticky header implementation
  useEffect(() => {
    let isSticky = false
    let ticking = false
    let lastScrollY = 0

    const applySticky = (header) => {
      // Get the scroll container's position and dimensions
      const table = tableRef.current
      const scrollContainer = scrollContainerRef.current
      if (table && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()
        const tableRect = table.getBoundingClientRect()
        const scrollLeft = scrollContainer.scrollLeft

        // Style the header (thead) element as a fixed container
        header.style.position = 'fixed'
        header.style.top = '0px'
        // Position at the container's left edge
        header.style.left = `${containerRect.left}px`
        // Use table width
        header.style.width = `${tableRect.width}px`
        header.style.zIndex = '1000'
        header.style.backgroundColor = 'rgb(249, 250, 251)'
        header.style.boxShadow = '0 2px 4px 0 rgb(0 0 0 / 0.1)'
        // Use clip-path to clip the header to container boundaries
        // Clip from right side: everything beyond containerRect.width
        header.style.clipPath = `inset(0 calc(100% - ${containerRect.width}px) 0 0)`

        // Apply transform to the tr element inside thead to shift horizontally
        const headerRow = header.querySelector('tr')
        if (headerRow) {
          headerRow.style.transform = `translateX(-${scrollLeft}px)`
          headerRow.style.willChange = 'transform'
        }
      }
    }

    const removeSticky = (header) => {
      header.style.position = ''
      header.style.top = ''
      header.style.left = ''
      header.style.width = ''
      header.style.zIndex = ''
      header.style.backgroundColor = ''
      header.style.boxShadow = ''
      header.style.clipPath = ''

      // Remove transform from the tr element
      const headerRow = header.querySelector('tr')
      if (headerRow) {
        headerRow.style.transform = ''
        headerRow.style.willChange = ''
      }
    }

    const updateStickyHeader = () => {
      if (!tableRef.current || !headerRef.current) return

      const header = headerRef.current
      const table = tableRef.current
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop

      // Determine if header should be sticky based on table position
      // When not sticky, check header position; when sticky, check table position
      let shouldBeSticky
      if (isSticky) {
        // If already sticky, check table position to see if we should unstick
        const tableRect = table.getBoundingClientRect()
        shouldBeSticky = tableRect.top < 0
      } else {
        // If not sticky, check header position to see if we should stick
        const headerRect = header.getBoundingClientRect()
        shouldBeSticky = headerRect.top <= 0
      }

      // Update if state has changed
      if (shouldBeSticky !== isSticky) {
        isSticky = shouldBeSticky

        if (isSticky) {
          applySticky(header)
        } else {
          removeSticky(header)
        }
      } else if (isSticky) {
        // If already sticky, update position (for resize/horizontal scroll)
        applySticky(header)
      }

      lastScrollY = currentScrollY
      ticking = false
    }

    // Throttled scroll handler using requestAnimationFrame
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateStickyHeader)
        ticking = true
      }
    }

    // Mobile-specific touch handlers
    let touchStartY = 0
    let touchEndY = 0

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY
    }

    const handleTouchMove = (e) => {
      // Force update during touch scrolling
      if (!ticking) {
        requestAnimationFrame(updateStickyHeader)
        ticking = true
      }
    }

    const handleTouchEnd = (e) => {
      touchEndY = e.changedTouches[0].clientY
      // Force update after touch ends
      setTimeout(() => {
        if (!ticking) {
          requestAnimationFrame(updateStickyHeader)
          ticking = true
        }
      }, 100)
    }

    // Intersection Observer as primary detection method
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const header = headerRef.current
          if (!header) return

          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            if (!isSticky) {
              isSticky = true
              applySticky(header)
            }
          } else if (entry.isIntersecting) {
            if (isSticky) {
              isSticky = false
              removeSticky(header)
            }
          }
        })
      },
      {
        threshold: [0, 0.1],
        rootMargin: '-1px 0px 0px 0px'
      }
    )

    // Set up all event listeners
    if (headerRef.current) {
      observer.observe(headerRef.current)
    }

    // Horizontal scroll listener for the table container
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    }

    // Multiple scroll event listeners for maximum compatibility
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('scroll', handleScroll, { passive: true })

    // Mobile touch events
    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })

    // Additional mobile events
    window.addEventListener('orientationchange', () => {
      setTimeout(handleScroll, 100)
    })

    window.addEventListener('resize', () => {
      setTimeout(handleScroll, 100)
    })

    // Initial check
    setTimeout(handleScroll, 100)

    // Cleanup
    return () => {
      observer.disconnect()
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('scroll', handleScroll)
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('orientationchange', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, []) // Run once on mount

  // Get records with pagination and filtering
  const {
    data: recordsData,
    isLoading: recordsLoading,
    error: recordsError
  } = useQuery(
    ['records', dataInput.id, currentPage, pageSize, searchTerm, filterColumn, filterValue, columnFilters, currentSort],
    async () => {
      // Use advanced filtering if column filters are active or sorting is applied
      if (columnFilters.length > 0 || currentSort) {
        const requestData = {
          filters: columnFilters,
          search_query: searchTerm || undefined,
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
          include_total_count: true
        }

        // Add sorting if specified
        if (currentSort) {
          requestData.sort_by = [currentSort]
        }

        return dataInputsService.filterRecords(dataInput.id, requestData)
      } else {
        // Use basic filtering for backward compatibility
        return dataInputsService.getRecords(dataInput.id, {
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          filter_column: filterColumn,
          filter_value: filterValue
        })
      }
    },
    {
      keepPreviousData: true,
      staleTime: 0, // Always consider data stale so it refetches more readily
      cacheTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      onError: (error) => {
        console.error('Error loading records:', error)
      }
    }
  )

  const records = recordsData?.records || []
  // Handle different response structures (basic vs advanced filtering)
  const totalRecords = recordsData?.total || recordsData?.total_count || 0
  const totalPages = Math.ceil(totalRecords / pageSize)

  // Filter to only show active attributes in the records table, sorted by display_order
  const activeAttributes = useMemo(() => {
    const filtered = dataInput?.attributes?.filter(attr => attr.is_active) || []
    return filtered.sort((a, b) => {
      // Sort by display_order first, then by id as fallback
      if (a.display_order !== b.display_order) {
        return a.display_order - b.display_order
      }
      return a.id - b.id
    })
  }, [dataInput?.attributes])

  // Helper function to determine column width class based on data type
  const getColumnWidthClass = (attribute) => {
    switch (attribute.data_type) {
      case 'Boolean':
        return 'column-short'
      case 'Number':
      case 'Decimal':
      case 'Date':
        return 'column-medium'
      case 'Phone Number':
      case 'Email':
        return 'column-long'
      case 'URL':
      case 'Text':
        return attribute.name.length > 15 ? 'column-extra-long' : 'column-long'
      case 'File':
      case 'Image':
      case 'Video':
      case 'Audio':
      case 'Document':
      case 'Archive':
        return 'column-extra-long' // Wider column for file types
      default:
        return 'column-medium'
    }
  }

  // Delete record mutation
  const deleteRecordMutation = useMutation(
    (recordId) => dataInputsService.deleteRecord(dataInput.id, recordId),
    {
      onSuccess: () => {
        toast.success('Record deleted successfully!')
        queryClient.removeQueries({
          predicate: (query) => {
            return query.queryKey[0] === 'records' && query.queryKey[1] === dataInput.id
          }
        })
        setShowDeleteModal(false)
        setSelectedRecord(null)
      },
      onError: (error) => {
        console.error('Error deleting record:', error)
        toast.error('Failed to delete record')
      }
    }
  )

  // Inline edit mutation
  const inlineEditMutation = useMutation(
    ({ recordId, attributeName, value }) => {
      const record = records.find(r => r.id === recordId)
      if (!record) throw new Error('Record not found')

      // Handle both response structures:
      // - Basic endpoint: record.value contains all value fields
      // - Filter endpoint: values are flattened into record (no record.value)
      const existingValue = record.value || activeAttributes.reduce((acc, attr) => {
        if (record[attr.name] !== undefined) {
          acc[attr.name] = record[attr.name]
        }
        return acc
      }, {})

      const updatedValue = {
        ...existingValue,
        [attributeName]: value
      }

      return dataInputsService.updateRecord(dataInput.id, recordId, { value: updatedValue })
    },
    {
      onSuccess: () => {
        toast.success('Record updated successfully!')
        queryClient.removeQueries({
          predicate: (query) => {
            return query.queryKey[0] === 'records' && query.queryKey[1] === dataInput.id
          }
        })
        setEditingCell(null)
      },
      onError: (error) => {
        console.error('Error updating record:', error)
        toast.error('Failed to update record')
        setEditingCell(null)
      }
    }
  )

  // Handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleSearch = (value) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleFilter = (column, value, operator = 'eq') => {
    setFilterColumn(column)
    setFilterValue(value)
    setFilterOperator(operator)
    setCurrentPage(1)

    // If we have a column and value, add it to columnFilters for advanced filtering
    if (column && value) {
      const newFilter = {
        column: column,
        operator: operator,
        value: value
      }

      // Remove any existing filter for this column and add the new one
      const updatedFilters = columnFilters.filter(f => f.column !== column)
      updatedFilters.push(newFilter)
      setColumnFilters(updatedFilters)
    } else if (column && !value) {
      // If no value, remove the filter for this column
      setColumnFilters(columnFilters.filter(f => f.column !== column))
    }
  }

  const handleDeleteRecord = (record) => {
    setSelectedRecord(record)
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    if (selectedRecord) {
      deleteRecordMutation.mutate(selectedRecord.id)
    }
  }

  // Bulk selection handlers
  const toggleBulkSelectionMode = () => {
    setBulkSelectionMode(!bulkSelectionMode)
    setSelectedRecords([])
  }

  const toggleRecordSelection = (record) => {
    setSelectedRecords(prev => {
      const isSelected = prev.some(r => r.id === record.id)
      if (isSelected) {
        return prev.filter(r => r.id !== record.id)
      } else {
        return [...prev, record]
      }
    })
  }

  const toggleSelectAll = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([])
    } else {
      setSelectedRecords([...records])
    }
  }

  const handleBulkOperations = () => {
    if (selectedRecords.length === 0) {
      toast.error('Please select at least one record')
      return
    }
    setShowBulkModal(true)
  }

  const handleBulkSuccess = () => {
    setSelectedRecords([])
    setBulkSelectionMode(false)
    setShowBulkModal(false)
  }

  // Column filter handlers
  const handleColumnFilter = (filter) => {
    setColumnFilters(prev => {
      const existingIndex = prev.findIndex(f => f.column === filter.column)
      if (existingIndex >= 0) {
        // Update existing filter
        const updated = [...prev]
        updated[existingIndex] = filter
        return updated
      } else {
        // Add new filter
        return [...prev, filter]
      }
    })
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleClearColumnFilter = (columnName) => {
    setColumnFilters(prev => prev.filter(f => f.column !== columnName))
    setCurrentPage(1) // Reset to first page when clearing filter
  }

  const clearAllColumnFilters = () => {
    setColumnFilters([])
    setCurrentPage(1)
    setShowColumnFilters(false) // Hide filters when clearing all
  }

  const toggleColumnFilters = () => {
    setShowColumnFilters(!showColumnFilters)
    if (showColumnFilters && columnFilters.length > 0) {
      // If hiding filters and there are active filters, clear them
      setColumnFilters([])
      setCurrentPage(1)
    }
  }

  // Get active filter for a column
  const getColumnFilter = (columnName) => {
    return columnFilters.find(f => f.column === columnName)
  }

  // Column sorting handlers
  const handleSortChange = (sortSpec) => {
    setCurrentSort(sortSpec)
    setCurrentPage(1) // Reset to first page when sorting
  }

  // Create virtual attributes for system columns to enable filtering
  const systemColumnAttributes = [
    {
      id: 'created_at',
      name: 'created_at',
      label: 'Created At',
      data_type: 'Datetime', // Backend expects datetime filtering for system columns
      is_system: true,
      display_as_date: true // Flag to show date picker instead of datetime picker
    },
    {
      id: 'created_by',
      name: 'created_by',
      label: 'Created By',
      data_type: 'Text',
      is_system: true
    },
    {
      id: 'updated_at',
      name: 'updated_at',
      label: 'Updated At',
      data_type: 'Datetime', // Backend expects datetime filtering for system columns
      is_system: true,
      display_as_date: true // Flag to show date picker instead of datetime picker
    },
    {
      id: 'updated_by',
      name: 'updated_by',
      label: 'Updated By',
      data_type: 'Text',
      is_system: true
    }
  ]

  // Inline edit handlers
  const handleStartEdit = (recordId, attributeName) => {
    setEditingCell({ recordId, attributeName })
  }

  const handleSaveEdit = async (recordId, attributeName, value) => {
    const attribute = activeAttributes.find(attr => attr.name === attributeName)
    if (!attribute) return

    let convertedValue = value

    if (value !== null && value !== undefined && value !== '') {
      switch (attribute.data_type) {
        case 'Number':
          convertedValue = Number(value)
          break
        case 'Decimal':
          convertedValue = parseFloat(value)
          break
        case 'Boolean':
          convertedValue = value === true || value === 'true' || value === 'on'
          break
        case 'Date':
        case 'Datetime':
          convertedValue = value
          break
        default:
          convertedValue = value
      }
    }

    return inlineEditMutation.mutateAsync({ recordId, attributeName, value: convertedValue })
  }

  const handleCancelEdit = () => {
    setEditingCell(null)
  }

  // Export functionality with advanced options
  const handleExportClick = () => {
    if (!activeAttributes || activeAttributes.length === 0) {
      toast.error('No active attributes defined for export')
      return
    }
    setShowExportModal(true)
  }

  const handleExport = async (exportOptions) => {
    let loadingToast
    try {
      loadingToast = toast.loading('Preparing export... This may take a moment for large datasets.')

      // Prepare current filters and search
      const currentFilters = columnFilters.length > 0 ? columnFilters : []
      const currentSearch = searchTerm || null

      // Use the new export service
      const result = await exportsService.exportData(dataInput.id, {
        format: exportOptions.format,
        includeHeaders: exportOptions.includeHeaders,
        includeMetadata: exportOptions.includeMetadata,
        includeDeleted: exportOptions.includeDeleted,
        selectedColumns: exportOptions.selectedColumns,
        filename: exportOptions.filename || `${dataInput.name}_export`,
        filters: currentFilters,
        searchQuery: currentSearch,
        dateRangeEnabled: exportOptions.dateRangeEnabled,
        dateFrom: exportOptions.dateFrom,
        dateTo: exportOptions.dateTo
      })

      // Dismiss loading toast
      toast.dismiss(loadingToast)

      if (result.success) {
        // Download the file
        exportsService.downloadBlob(result.blob, result.filename)
        toast.success(`Successfully exported ${result.recordCount} records!`)
      } else {
        toast.error('Export failed. Please try again.')
      }
    } catch (error) {
      // Dismiss loading toast if it exists
      if (loadingToast) {
        toast.dismiss(loadingToast)
      }
      console.error('Export failed:', error)
      toast.error(error.message || 'Export failed. Please try again.')
    }
  }

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  // Get filter column options
  const filterColumnOptions = useMemo(() => {
    if (!activeAttributes) return []

    // Combine data input attributes with system column attributes
    const allAttributes = [
      ...activeAttributes.map(attr => ({
        value: attr.name,
        label: attr.label || attr.name
      })),
      ...systemColumnAttributes.map(attr => ({
        value: attr.name,
        label: attr.label || attr.name
      }))
    ]

    return allAttributes
  }, [activeAttributes])

  // Get available operators for a column based on its data type
  const getOperatorsForColumn = (columnName) => {
    // Find the attribute to get its data type
    const attribute = [...(activeAttributes || []), ...systemColumnAttributes].find(attr => attr.name === columnName)
    const dataType = (attribute?.data_type || 'text').toLowerCase()



    // Define operators based on data type
    const operatorsByType = {
      text: [
        { value: 'eq', label: 'Equals' },
        { value: 'ne', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does Not Contain' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' }
      ],
      number: [
        { value: 'eq', label: 'Equals' },
        { value: 'ne', label: 'Not Equals' },
        { value: 'gt', label: 'Greater Than' },
        { value: 'gte', label: 'Greater Than or Equal' },
        { value: 'lt', label: 'Less Than' },
        { value: 'lte', label: 'Less Than or Equal' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' }
      ],
      date: [
        { value: 'date_eq', label: 'Date Equals' },
        { value: 'date_before', label: 'Before' },
        { value: 'date_after', label: 'After' },
        { value: 'date_between', label: 'Between' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' }
      ],
      datetime: [
        { value: 'date_eq', label: 'Date Equals' },
        { value: 'date_before', label: 'Before' },
        { value: 'date_after', label: 'After' },
        { value: 'date_between', label: 'Between' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' }
      ],
      boolean: [
        { value: 'is_true', label: 'Is True' },
        { value: 'is_false', label: 'Is False' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' }
      ]
    }

    return operatorsByType[dataType] || operatorsByType.text
  }

  if (!activeAttributes || activeAttributes.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No active attributes</h3>
        <p className="mt-1 text-sm text-gray-500">
          This data input doesn't have any active attributes. Please activate attributes first.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Records ({totalRecords})
          </h3>
          <p className="text-xs text-gray-500 mt-1 hidden sm:block">
            Click on any cell to edit inline • Press Enter to save • Press Escape to cancel
          </p>
          <p className="text-xs text-gray-500 mt-1 sm:hidden">
            Tap cell to edit • Enter to save • Escape to cancel
          </p>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
          {/* Bulk Operations */}
          {bulkSelectionMode ? (
            <>
              <div className="text-sm text-gray-600">
                {selectedRecords.length} selected
              </div>
              <button
                onClick={handleBulkOperations}
                className="btn-primary btn-sm flex items-center"
                disabled={selectedRecords.length === 0}
              >
                <Settings className="h-4 w-4 mr-2" />
                Bulk Actions
              </button>
              <button
                onClick={toggleBulkSelectionMode}
                className="btn-outline btn-sm flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
            </>
          ) : (
            <>
              {/* Export button - for admin, manager, and area manager roles */}
              {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.MANAGER || user?.role === USER_ROLES.AREA_MANAGER) && (
                <button
                  onClick={handleExportClick}
                  className="btn-outline btn-sm flex items-center justify-center"
                  disabled={records.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              )}
              <button
                onClick={toggleBulkSelectionMode}
                className="btn-outline btn-sm flex items-center justify-center"
                disabled={records.length === 0}
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Bulk Select</span>
                <span className="sm:hidden">Bulk</span>
              </button>
              <button
                onClick={toggleColumnFilters}
                className={`btn-outline btn-sm flex items-center justify-center ${
                  showColumnFilters ? 'bg-primary-100 text-primary-700 border-primary-300' : ''
                }`}
                disabled={records.length === 0}
              >
                <Filter className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{showColumnFilters ? 'Hide Filters' : 'Create Filters'}</span>
                <span className="sm:hidden">Filter</span>
              </button>
              {/* Bulk Upload button - disabled for user role only */}
              {user?.role !== USER_ROLES.USER && (
                <Link
                  to={`/data-inputs/${dataInput.id}/records/bulk-upload`}
                  className="btn-outline btn-sm flex items-center justify-center"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Bulk Upload</span>
                  <span className="sm:hidden">Upload</span>
                </Link>
              )}
              <Link
                to={`/data-inputs/${dataInput.id}/records/create`}
                className="btn-primary btn-sm flex items-center justify-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Add Record</span>
                <span className="sm:hidden">Add</span>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search records..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Filter Column */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterColumn}
              onChange={(e) => {
                setFilterColumn(e.target.value)
                // Reset operator when column changes
                if (e.target.value) {
                  const operators = getOperatorsForColumn(e.target.value)
                  setFilterOperator(operators[0]?.value || 'eq')
                }
              }}
              className="input pl-10"
            >
              <option value="">Select column to filter</option>
              {filterColumnOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Filter Operator */}
          {filterColumn && (
            <div className="relative">
              <select
                value={filterOperator}
                onChange={(e) => setFilterOperator(e.target.value)}
                className="input"
              >
                {getOperatorsForColumn(filterColumn).map(operator => (
                  <option key={operator.value} value={operator.value}>
                    {operator.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Filter Value */}
          <div className="flex space-x-2">
            {!['is_null', 'is_not_null', 'is_true', 'is_false'].includes(filterOperator) && (
              <input
                type={filterColumn === 'created_at' || filterColumn === 'updated_at' ? 'datetime-local' : 'text'}
                placeholder="Filter value..."
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                disabled={!filterColumn}
                className="input flex-1"
              />
            )}
            {filterColumn && (filterValue || ['is_null', 'is_not_null', 'is_true', 'is_false'].includes(filterOperator)) && (
              <button
                onClick={() => handleFilter(filterColumn, filterValue, filterOperator)}
                className="btn-primary btn-sm flex items-center"
              >
                Apply Filter
              </button>
            )}
            {(filterColumn || filterValue) && (
              <button
                onClick={() => {
                  setFilterColumn('')
                  setFilterValue('')
                  setFilterOperator('eq')
                  // Remove all filters
                  setColumnFilters([])
                }}
                className="btn-outline btn-sm flex items-center"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Active Column Filters */}
        {showColumnFilters && columnFilters.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                <div className="flex flex-wrap gap-2">
                  {columnFilters.map((filter, index) => {
                    const attribute = activeAttributes.find(attr => attr.name === filter.column)
                    const operatorLabel = {
                      'eq': '=',
                      'ne': '≠',
                      'gt': '>',
                      'gte': '≥',
                      'lt': '<',
                      'lte': '≤',
                      'contains': '⊃',
                      'not_contains': '⊅',
                      'starts_with': '⊢',
                      'ends_with': '⊣',
                      'in': '∈',
                      'not_in': '∉',
                      'between': '⟷',
                      'is_null': '∅',
                      'is_not_null': '≠∅'
                    }[filter.operator] || filter.operator

                    return (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary-100 text-primary-800"
                      >
                        <span className="font-medium">{attribute?.label || filter.column}</span>
                        <span className="mx-1">{operatorLabel}</span>
                        {!['is_null', 'is_not_null'].includes(filter.operator) && (
                          <span>
                            {Array.isArray(filter.value)
                              ? filter.value.join(', ')
                              : String(filter.value)
                            }
                          </span>
                        )}
                        <button
                          onClick={() => handleClearColumnFilter(filter.column)}
                          className="ml-1 text-primary-600 hover:text-primary-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )
                  })}
                </div>
              </div>
              <button
                onClick={clearAllColumnFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Records Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {recordsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-500">Loading records...</span>
          </div>
        ) : records.length > 0 ? (
          <div className="overflow-x-auto" ref={scrollContainerRef}>
            <table className="records-table" ref={tableRef}>
              <thead className="bg-gray-50" ref={headerRef}>
                <tr>
                  {bulkSelectionMode && (
                    <th className="column-checkbox">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center justify-center w-5 h-5"
                      >
                        {selectedRecords.length === records.length && records.length > 0 ? (
                          <CheckSquare className="h-4 w-4 text-primary-600" />
                        ) : (
                          <Square className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </th>
                  )}
                  {activeAttributes.map((attribute) => (
                    <th key={attribute.id} className={getColumnWidthClass(attribute)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 min-w-0">
                          <span className="truncate">{attribute.label || attribute.name}</span>
                          <ColumnSort
                            column={attribute.name}
                            currentSort={currentSort}
                            onSortChange={handleSortChange}
                          />
                        </div>
                        {showColumnFilters && (
                          <ColumnFilter
                            attribute={attribute}
                            value={getColumnFilter(attribute.name)?.value}
                            operator={getColumnFilter(attribute.name)?.operator}
                            onFilterChange={handleColumnFilter}
                            onClear={handleClearColumnFilter}
                            dataInputId={dataInput.id}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                  {systemColumnAttributes.map((systemAttr) => (
                    <th key={systemAttr.name} className="column-medium">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 min-w-0">
                          <span className="truncate">{systemAttr.label}</span>
                          <ColumnSort
                            column={systemAttr.name}
                            currentSort={currentSort}
                            onSortChange={handleSortChange}
                          />
                        </div>
                        {showColumnFilters && (
                          <ColumnFilter
                            attribute={systemAttr}
                            value={getColumnFilter(systemAttr.name)?.value}
                            operator={getColumnFilter(systemAttr.name)?.operator}
                            onFilterChange={handleColumnFilter}
                            onClear={handleClearColumnFilter}
                            dataInputId={dataInput.id}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                  {!bulkSelectionMode && (
                    <th className="column-actions">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => (
                  <tr
                    key={record.id}
                    className={`hover:bg-gray-50 ${
                      editingCell?.recordId === record.id ? 'bg-blue-50 border-l-4 border-blue-400' : ''
                    } ${
                      bulkSelectionMode && selectedRecords.some(r => r.id === record.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    {bulkSelectionMode && (
                      <td className="column-checkbox">
                        <button
                          onClick={() => toggleRecordSelection(record)}
                          className="flex items-center justify-center w-5 h-5"
                        >
                          {selectedRecords.some(r => r.id === record.id) ? (
                            <CheckSquare className="h-4 w-4 text-primary-600" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                    )}
                    {activeAttributes.map((attribute) => (
                      <td
                        key={attribute.id}
                        className={`${getColumnWidthClass(attribute)} ${
                          editingCell?.recordId === record.id && editingCell?.attributeName === attribute.name
                            ? 'inline-edit-cell editing'
                            : 'inline-edit-cell'
                        }`}
                      >
                        {attribute.is_auto_generated ? (
                          <div className="flex items-center">
                            <Sparkles className="h-3 w-3 text-gray-500 mr-1 flex-shrink-0" />
                            <span className="truncate">
                              {record.value?.[attribute.name] || '-'}
                            </span>
                          </div>
                        ) : (
                          <InlineEditCell
                            value={record.value?.[attribute.name] || record[attribute.name]}
                            attribute={attribute}
                            isEditing={editingCell?.recordId === record.id && editingCell?.attributeName === attribute.name}
                            onStartEdit={() => handleStartEdit(record.id, attribute.name)}
                            onSave={(value) => handleSaveEdit(record.id, attribute.name, value)}
                            onCancel={handleCancelEdit}
                          />
                        )}
                      </td>
                    ))}
                    <td className="column-medium">
                      <div className="truncate">
                        {new Date(record.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="column-medium">
                      <div className="truncate">
                        {record.created_by || '-'}
                      </div>
                    </td>
                    <td className="column-medium">
                      <div className="truncate">
                        {record.updated_at ? new Date(record.updated_at).toLocaleDateString() : '-'}
                      </div>
                    </td>
                    <td className="column-medium">
                      <div className="truncate">
                        {record.updated_by || '-'}
                      </div>
                    </td>
                    {!bulkSelectionMode && (
                      <td className="column-actions">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => handleDeleteRecord(record)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Record"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No records found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterValue ? 'No records match your search criteria.' : 'Get started by creating your first record.'}
            </p>
            <div className="mt-6">
              <Link
                to={`/data-inputs/${dataInput.id}/records/create`}
                className="btn-primary btn-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Link>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                <div className="text-sm text-gray-700">
                  <span className="hidden sm:inline">Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results</span>
                  <span className="sm:hidden">{(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}</span>
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1 w-full sm:w-auto"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded touch-manipulation"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-2 sm:px-3 py-2 border text-sm font-medium rounded touch-manipulation ${
                        pageNum === currentPage
                          ? 'bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded touch-manipulation"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Record
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this record? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={confirmDelete}
                  disabled={deleteRecordMutation.isLoading}
                >
                  {deleteRecordMutation.isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Record'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteRecordMutation.isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Operations Modal */}
      <BulkOperationsModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedRecords={selectedRecords}
        dataInput={dataInput}
        onSuccess={handleBulkSuccess}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataInput={dataInput}
        onExport={handleExport}
      />
    </div>
  )
}

export default RecordsTab